// V5.8 VERROUILLAGE POST-SIGNATURE : Vue de Gestion Active
// Cette page est affichée UNIQUEMENT pour les missions avec mandat signé
// Elle remplace TOTALEMENT le tunnel de saisie - pas de retour possible

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { 
  Home, Shield, Zap, CheckCircle, FileText, Upload, Download,
  ArrowLeft, Loader2, Clock, AlertCircle, Calendar, 
  MapPin, Building2, RefreshCw, Menu, User, Sparkles, Eye, TrendingDown
} from 'lucide-react';

// Import des composants layout
import { ContextualSidebar, MobileMenu } from '../../components/layout/ContextualSidebar';

// Configuration des types de mission
const MISSION_TYPE_CONFIG = {
  demenagement: {
    title: 'Mission Déménagement',
    subtitle: 'Gestion de votre dossier',
    icon: Home,
    color: 'cyan',
    bgGradient: 'from-cyan-500/20 to-blue-500/20',
    expertMessage: "Votre expert L'EFFACEUR analyse actuellement vos contrats pour lancer les transferts et résiliations."
  },
  succession: {
    title: 'Mission Héritage',
    subtitle: 'Suivi de votre dossier',
    icon: Shield,
    color: 'magenta',
    bgGradient: 'from-magenta-500/20 to-purple-500/20',
    expertMessage: "Votre expert L'EFFACEUR traite vos demandes de résiliation auprès des organismes avec la plus grande bienveillance."
  },
  audit: {
    title: 'Mission Audit',
    subtitle: 'Résultats de votre analyse',
    icon: Zap,
    color: 'success',
    bgGradient: 'from-success/20 to-green-500/20',
    expertMessage: "Votre expert L'EFFACEUR identifie les économies potentielles sur vos abonnements."
  }
};

const MissionManagementView = () => {
  const { missionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Récupérer le mission_id depuis les query params si pas dans l'URL
  const searchParams = new URLSearchParams(location.search);
  const queryMissionId = searchParams.get('mission') || missionId;
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);
  const [mandat, setMandat] = useState(null);
  const [missionType, setMissionType] = useState('demenagement');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [downloadingMandat, setDownloadingMandat] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Charger les données utilisateur et mission
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Charger l'utilisateur
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setUser(userRes.data);
      
      // V5.10 : D'abord récupérer tous les mandats de l'utilisateur
      let userMandat = null;
      try {
        const mandatRes = await axios.get(`${API}/mandats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        // Trouver le mandat signé le plus récent
        userMandat = mandatRes.data.mandats?.find(
          m => m.status === 'signed' || m.signed === true
        );
        if (userMandat) {
          console.log('V5.10: Mandat trouvé:', userMandat.mandat_id);
          setMandat(userMandat);
        }
      } catch (e) {
        console.log('Pas de mandat trouvé via /mandats');
      }
      
      // V5.8 : Vérifier d'abord si le mandat est signé via /missions/active
      const activeMissionsRes = await axios.get(`${API}/missions/active`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      // Trouver la mission correspondante ou la première avec mandat signé
      let currentMission = activeMissionsRes.data.missions?.find(
        m => m.mission_id === queryMissionId
      );
      
      // V5.10 : Si pas de mission avec ce query param, prendre celle avec mandat signé
      if (!currentMission) {
        currentMission = activeMissionsRes.data.missions?.find(
          m => m.has_mandat === true
        );
      }
      
      // V5.8 VERROUILLAGE STRICT : Si pas de mandat signé, rediriger vers le tunnel
      if (!currentMission || currentMission.has_mandat !== true) {
        console.log('V5.8 VERROUILLAGE: Mandat non signé, redirection vers le tunnel');
        const type = currentMission?.mission_type || 'demenagement';
        const missionIdToUse = currentMission?.mission_id || queryMissionId;
        navigate(`/missions/${type}?mission=${missionIdToUse}`);
        return;
      }
      
      // Charger la mission spécifique
      const missionIdToFetch = currentMission.mission_id;
      try {
        const missionRes = await axios.get(`${API}/missions/${missionIdToFetch}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        if (missionRes.data.mission) {
          setMission(missionRes.data.mission);
          setMissionType(missionRes.data.mission.mission_type || 'demenagement');
          
          // V5.10 : Si mandat dans la réponse mission, l'utiliser
          if (missionRes.data.mission.mandat) {
            setMandat(missionRes.data.mission.mandat);
          }
        }
      } catch (missionError) {
        console.log('Erreur chargement mission details:', missionError);
        // Utiliser les données de la mission active
        setMission({
          mission_id: missionIdToFetch,
          mission_type: currentMission.mission_type,
          status: currentMission.status,
          has_mandat: currentMission.has_mandat
        });
        setMissionType(currentMission.mission_type || 'demenagement');
      }
      
    } catch (error) {
      console.error('Erreur chargement:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast.error('Mission non trouvée');
        navigate('/dashboard?tab=missions');
      }
    } finally {
      setLoading(false);
    }
  }, [queryMissionId, navigate]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Upload d'un document complémentaire
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', 'complementaire');
    formData.append('mission_id', queryMissionId);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      toast.success(`Document "${file.name}" envoyé à votre expert !`);
      
      // Recharger les données de la mission
      await fetchData();
      
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingDoc(null);
    }
  };
  
  // Télécharger le mandat signé (PDF) - V5.10 FIX
  const handleDownloadMandat = async () => {
    // V5.10: Vérification robuste du mandat_id
    const mandatId = mandat?.mandat_id;
    if (!mandatId) {
      console.error('V5.10: mandat_id non disponible. État mandat:', mandat);
      toast.error('Mandat non trouvé. Veuillez rafraîchir la page.');
      return;
    }
    
    console.log('V5.10: Téléchargement PDF pour mandat_id:', mandatId);
    setDownloadingMandat(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/mandats/${mandatId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob',
        withCredentials: true
      });
      
      // V5.10: Vérifier que la réponse est bien un PDF
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/pdf')) {
        console.error('V5.10: Type de contenu inattendu:', contentType);
      }
      
      // V5.10: Créer le blob avec le type MIME correct
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mandat_leffaceur_${mandatId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Mandat téléchargé !');
      console.log('V5.10: PDF téléchargé avec succès');
      
    } catch (error) {
      console.error('V5.10 Erreur téléchargement:', error);
      const errorMessage = error.response?.status === 404 
        ? 'Mandat introuvable. Veuillez contacter le support.'
        : error.response?.status === 403
        ? 'Accès non autorisé à ce mandat.'
        : 'Erreur lors du téléchargement. Veuillez réessayer.';
      toast.error(errorMessage);
    } finally {
      setDownloadingMandat(false);
    }
  };
  
  const config = MISSION_TYPE_CONFIG[missionType] || MISSION_TYPE_CONFIG.demenagement;
  const Icon = config.icon;
  
  // Date de signature du mandat
  const signedDate = mandat?.signed_at 
    ? new Date(mandat.signed_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Date non disponible';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-mono text-sm">Chargement de votre espace expert...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-void flex">
      {/* Sidebar Desktop */}
      <ContextualSidebar 
        context="mission-management"
        missionType={missionType}
        user={user}
        onLogout={() => {
          localStorage.removeItem('token');
          navigate('/login');
        }}
      />
      
      {/* Menu Mobile */}
      <MobileMenu 
        isOpen={mobileMenuOpen}
        setIsOpen={setMobileMenuOpen}
        context="mission-management"
        missionType={missionType}
        user={user}
        onLogout={() => {
          localStorage.removeItem('token');
          navigate('/login');
        }}
      />
      
      {/* Contenu Principal */}
      <div className="flex-1 flex flex-col">
        {/* Header Mobile */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-orbitron text-sm font-bold text-success">Espace Expert</h1>
          <div className="w-10" />
        </header>
        
        {/* Header Desktop */}
        <header className="hidden md:block border-b border-gray-800 bg-void/80 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/dashboard?tab=missions"
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </Link>
              <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-success" />
              </div>
              <div>
                <h1 className="font-orbitron text-lg font-bold text-white">{config.title}</h1>
                <p className="text-gray-500 text-xs">Espace Expert L'EFFACEUR</p>
              </div>
            </div>
            
            {/* Badge Dossier Validé */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-success/20 border border-success/50">
              <CheckCircle className="w-5 h-5 text-success" />
              <span className="text-sm font-bold text-success">
                Dossier validé & Mandat signé
              </span>
            </div>
          </div>
        </header>
        
        {/* Contenu */}
        <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
          <div className="space-y-6">
            
            {/* 1. CONFIRMATION VISUELLE - Badge vert */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 border-2 border-success/50 bg-success/5"
              data-testid="validation-badge"
            >
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-success" />
                </div>
                <div className="text-center md:text-left">
                  <h2 className="font-orbitron text-2xl font-bold text-success mb-1">
                    Dossier validé & Mandat signé
                  </h2>
                  <p className="text-gray-400">
                    Votre mandat de gestion a été signé le <span className="text-white font-mono">{signedDate}</span>
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* 2. ACCÈS AU DOCUMENT - Bouton télécharger */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 border border-cyan-400/30"
              data-testid="mandat-download-section"
            >
              <h3 className="font-orbitron text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                Votre Mandat de Gestion
              </h3>
              
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-cyan-400/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Mandat de gestion signé</p>
                    <p className="text-gray-500 text-sm">Document PDF officiel</p>
                  </div>
                </div>
                
                <Button
                  onClick={handleDownloadMandat}
                  disabled={downloadingMandat}
                  className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron w-full md:w-auto"
                  data-testid="download-mandat-btn"
                >
                  {downloadingMandat ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Télécharger mon Mandat signé (PDF)
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-gray-500 text-xs mt-3 text-center">
                Ce document est une obligation légale. Conservez-le précieusement.
              </p>
            </motion.div>
            
            {/* 3. STATUT DE L'EXPERT - Message dynamique */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 border border-magenta-400/30 bg-magenta-400/5"
              data-testid="expert-status-section"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-magenta-400/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-7 h-7 text-magenta-400" />
                </div>
                <div>
                  <h3 className="font-orbitron text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-magenta-400" />
                    Statut de votre Expert L'EFFACEUR
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {config.expertMessage}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-magenta-400 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Traitement en cours...</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* 4. TIMELINE DE MISSION */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 border border-gray-700"
              data-testid="mission-timeline"
            >
              <h3 className="font-orbitron text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Suivi de votre Mission
              </h3>
              
              <div className="space-y-4">
                {/* Étape 1 - Mandat signé */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-success" />
                    </div>
                    <div className="w-0.5 h-12 bg-success/50 mt-2" />
                  </div>
                  <div className="pt-2">
                    <p className="text-white font-medium">Mandat signé</p>
                    <p className="text-gray-500 text-sm">{signedDate}</p>
                  </div>
                </div>
                
                {/* Étape 2 - Analyse en cours */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center">
                      <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
                    </div>
                    <div className="w-0.5 h-12 bg-gray-700 mt-2" />
                  </div>
                  <div className="pt-2">
                    <p className="text-cyan-400 font-medium">Analyse des contrats en cours</p>
                    <p className="text-gray-500 text-sm">Votre expert vérifie vos abonnements actuels</p>
                  </div>
                </div>
                
                {/* Étape 3 - À venir */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div className="pt-2">
                    <p className="text-gray-500 font-medium">Confirmation des résiliations</p>
                    <p className="text-gray-600 text-sm">À venir - sous 15-30 jours</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* 5. ZONE DE DÉPÔT COMPLÉMENTAIRE */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 border border-orange-400/30"
              data-testid="documents-upload-section"
            >
              <h3 className="font-orbitron text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-400" />
                Documents complémentaires
              </h3>
              
              <p className="text-gray-400 text-sm mb-4">
                Si notre expert a besoin d'une pièce supplémentaire, déposez-la ici. 
                <span className="text-orange-400"> Vous n'avez pas besoin de reprendre le questionnaire.</span>
              </p>
              
              <div 
                className="p-8 border-2 border-dashed border-gray-700 rounded-lg hover:border-orange-400/50 transition-all cursor-pointer bg-gray-800/20"
                onClick={() => document.getElementById('doc-upload-input')?.click()}
              >
                <input
                  id="doc-upload-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  disabled={uploadingDoc !== null}
                />
                
                <div className="text-center">
                  {uploadingDoc ? (
                    <>
                      <Loader2 className="w-12 h-12 text-orange-400 mx-auto mb-3 animate-spin" />
                      <p className="text-white font-medium">Envoi de "{uploadingDoc}"...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-white font-medium mb-1">Glissez-déposez un fichier ici</p>
                      <p className="text-gray-500 text-sm">ou cliquez pour sélectionner</p>
                      <p className="text-gray-600 text-xs mt-2">PDF, JPG, PNG, DOC (max 10 Mo)</p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
            
            {/* Récapitulatif du Dossier (simplifié) */}
            {mission?.details && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass-card p-6 border border-gray-700"
                data-testid="mission-summary"
              >
                <h3 className="font-orbitron text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gray-400" />
                  Récapitulatif du Dossier
                </h3>
                
                {missionType === 'demenagement' && (
                  <div className="space-y-4">
                    {/* Adresses */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {mission.details.current_address && (
                        <div className="p-4 bg-gray-800/50 rounded-lg">
                          <p className="text-gray-500 text-xs font-mono mb-1">ANCIENNE ADRESSE</p>
                          <p className="text-white">{mission.details.current_address.address}</p>
                          <p className="text-gray-400 text-sm">
                            {mission.details.current_address.postal_code} {mission.details.current_address.city}
                          </p>
                        </div>
                      )}
                      
                      {mission.details.new_address && (
                        <div className="p-4 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                          <p className="text-cyan-400 text-xs font-mono mb-1">NOUVELLE ADRESSE</p>
                          <p className="text-white">{mission.details.new_address.address}</p>
                          <p className="text-gray-400 text-sm">
                            {mission.details.new_address.postal_code} {mission.details.new_address.city}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* V5.9 : Plan d'Action validé */}
                    {(mission.details.selected_actions || mandat?.selected_actions) && (
                      <div className="p-4 bg-success/10 rounded-lg border border-success/30">
                        <p className="text-success text-xs font-mono mb-3 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" />
                          PLAN D'ACTION EN COURS DE TRAITEMENT
                        </p>
                        <div className="space-y-2">
                          {(mission.details.selected_actions || mandat?.selected_actions || []).map((action, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-success" />
                                <span className="text-white text-sm">
                                  {action.action_type === 'optimisation' 
                                    ? `Souscription ${action.new_provider || action.contract_name}`
                                    : `Transfert ${action.provider || action.contract_name}`
                                  }
                                </span>
                              </div>
                              {action.estimated_savings > 0 && (
                                <span className="text-success text-sm font-mono">-{action.estimated_savings}€/mois</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Contrats sans Plan d'Action (affichage simple) */}
                    {mission.details.contracts_to_transfer && !mission.details.selected_actions && (
                      <div className="p-4 bg-gray-800/50 rounded-lg">
                        <p className="text-gray-500 text-xs font-mono mb-2">CONTRATS À GÉRER</p>
                        <div className="flex flex-wrap gap-2">
                          {mission.details.contracts_to_transfer.map((c, idx) => (
                            <span key={idx} className="px-2 py-1 bg-cyan-400/20 text-cyan-400 rounded text-sm">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {missionType === 'succession' && mission.details.deceased && (
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-500 text-xs font-mono mb-1">DÉFUNT</p>
                    <p className="text-white font-medium">{mission.details.deceased.name}</p>
                  </div>
                )}
              </motion.div>
            )}
            
            {/* Bouton Retour */}
            <div className="flex justify-center pt-4">
              <Link to="/dashboard?tab=missions">
                <Button variant="outline" className="border-gray-600 text-gray-400 hover:border-cyan-400 hover:text-cyan-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à Mes Missions
                </Button>
              </Link>
            </div>
          </div>
        </main>
        
        {/* Footer Signature Toulouse */}
        <footer className="border-t border-gray-800/50 py-4 text-center">
          <p className="text-sm flex items-center justify-center gap-2 font-medium">
            <span className="text-gray-500">Fait avec le</span>
            <span className="text-[#FF3366] animate-pulse text-lg">❤️</span>
            <span className="text-gray-500">à</span>
            <span className="text-cyan-400 font-orbitron">Toulouse</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default MissionManagementView;
